/**
 * # Spotlight
 *
 * Dims the page except for one element, which keeps its natural place in the document. Everywhere
 * else swallows clicks; pass `interactive` to let them through to the highlighted control too, so
 * the only thing the user can reach is that control.
 *
 * Built for product tours and first-run coachmarks, where the highlighted control is often the very
 * thing the user is being asked to press.
 *
 * ## Why a clip-path rather than a scrim plus `z-index`
 *
 * The obvious approach — one full-screen scrim, with the highlighted element raised above it — only
 * works when nothing between that element and the page root creates a stacking context. A
 * `position: sticky` ancestor with a `z-index` (a sticky toolbar, a sticky footer) does create one,
 * and a descendant's `z-index` is then resolved *inside* it, so the element can never climb past a
 * full-screen scrim no matter how large the value. It stays dimmed, and — worse — unclickable.
 *
 * This paints a single fixed scrim and cuts a hole in it instead. Clipped-away regions are not
 * hit-tested, so the hole is genuinely transparent to the pointer while the painted area still
 * swallows clicks.
 *
 * The hole tracks the target through layout changes, including the ones that move it without
 * resizing it — a sibling loading a longer label, an ancestor narrowing, a nested scroll.
 *
 * ## Props
 *
 * - `target` (HTMLElement | null): the element to leave lit. Renders nothing while `null`, so a
 *   target that mounts late — a panel still fetching — degrades to no dim rather than a hole in the
 *   wrong place. Take it with a callback ref held in state, not `useRef`: the component has to
 *   re-render when the element appears or changes, and a ref mutation does not do that.
 * - `halo` (number): breathing room around the target, in px. Default `4`.
 * - `interactive` (boolean): let clicks reach the lit control. Default `false`, which covers the
 *   hole with a transparent blocker. Only turn it on where the control's own action is the way
 *   forward: anything it opens — a menu, an inline editor — lands outside the hole and under the
 *   scrim, where it reads as dimmed and dead.
 * - `className` (string): extra classes for the scrim.
 *
 * ## Usage
 *
 * ```jsx
 * const [gradeButton, setGradeButton] = useState<HTMLElement | null>(null);
 *
 * <button ref={setGradeButton}>Grade</button>
 * {isTouring && <Spotlight target={gradeButton} />}
 * ```
 */

import React, {useLayoutEffect, useState} from 'react';
import {createPortal} from 'react-dom';

interface Hole {
  top: number;
  left: number;
  width: number;
  height: number;
  /** The target's own corner radius, so the hole follows its silhouette rather than its box. */
  radius: number;
}

interface Props {
  target: HTMLElement | null;
  halo?: number;
  interactive?: boolean;
  /** Overrides the target's own corner radius. */
  radius?: number;
  className?: string;
}

const isSameHole = (a: Hole | null, b: Hole | null) =>
  a === b ||
  (a !== null &&
    b !== null &&
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height &&
    a.radius === b.radius);

/**
 * `border-radius` resolves to px for a laid-out element, but a percentage stays a percentage, so
 * a pill (`50%`) has to be resolved against the box itself.
 */
const readRadius = (node: HTMLElement, width: number, height: number): number => {
  const raw = window.getComputedStyle(node).borderTopLeftRadius;
  const value = raw.endsWith('%')
    ? (parseFloat(raw) / 100) * Math.min(width, height)
    : parseFloat(raw);
  if (!Number.isFinite(value) || value <= 0) return 0;
  // A radius past half the shorter side has no meaning; the browser clamps it the same way.
  return Math.min(value, Math.min(width, height) / 2);
};

/** A detached or `display: none` element measures 0×0; treated as absent so no hole is cut. */
const readHole = (node: HTMLElement | null): Hole | null => {
  if (!node) return null;
  const {top, left, width, height} = node.getBoundingClientRect();
  if (width === 0 && height === 0) return null;
  return {top, left, width, height, radius: readRadius(node, width, height)};
};

/**
 * The viewport with the target punched out of it: two subpaths wound under `evenodd`, so the inner
 * one clears. A rounded inner rect is why this is `path()` and not `polygon()`, which draws only
 * straight lines and so can only ever cut a hard-cornered box around a rounded control.
 */
const keyhole = (
  {top, left, width, height, radius}: Hole,
  halo: number,
  viewport: {width: number; height: number},
  override?: number
) => {
  const t = Math.max(0, top - halo);
  const l = Math.max(0, left - halo);
  const r = Math.min(viewport.width, left + width + halo);
  const b = Math.min(viewport.height, top + height + halo);
  const outer = `M0 0 H${viewport.width} V${viewport.height} H0 Z`;
  // A target scrolled entirely out of view clamps to nothing; cutting it anyway inverts the
  // rectangle and punches a hole in the wrong corner. Dim everything instead.
  if (r <= l || b <= t) return `path(evenodd, "${outer}")`;

  // The halo grows the box, so the radius grows with it to stay concentric with the control.
  const grown = override ?? (radius > 0 ? radius + halo : 0);
  const rad = Math.min(grown, (r - l) / 2, (b - t) / 2);

  const inner =
    rad <= 0
      ? `M${l} ${t} H${r} V${b} H${l} Z`
      : [
          `M${l + rad} ${t}`,
          `H${r - rad}`,
          `A${rad} ${rad} 0 0 1 ${r} ${t + rad}`,
          `V${b - rad}`,
          `A${rad} ${rad} 0 0 1 ${r - rad} ${b}`,
          `H${l + rad}`,
          `A${rad} ${rad} 0 0 1 ${l} ${b - rad}`,
          `V${t + rad}`,
          `A${rad} ${rad} 0 0 1 ${l + rad} ${t}`,
          'Z'
        ].join(' ');

  return `path(evenodd, "${outer} ${inner}")`;
};

export const Spotlight = ({
  target,
  halo = 4,
  interactive = false,
  radius,
  className = ''
}: Props) => {
  const [hole, setHole] = useState<Hole | null>(null);
  // `path()` takes no percentages, so the scrim's own box has to be measured.
  const [viewport, setViewport] = useState(() => ({
    width: window.innerWidth,
    height: window.innerHeight
  }));

  // Layout, not passive: the scrim renders nothing until the first measurement, so deferring it
  // past paint shows one frame of undimmed page on every step.
  //
  // Most things that move the target never resize it, so observing it alone misses them; the
  // ancestor chain catches them, as `AnchoredFixedPosition` does. Scroll is captured, not bubbled.
  useLayoutEffect(() => {
    if (!target) {
      return undefined;
    }

    const measure = () => {
      setViewport((current) =>
        current.width === window.innerWidth && current.height === window.innerHeight
          ? current
          : {width: window.innerWidth, height: window.innerHeight}
      );
      setHole((current) => {
        const next = readHole(target);
        return isSameHole(current, next) ? current : next;
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    for (
      let node: HTMLElement | null = target;
      node && node !== document.body;
      node = node.parentElement
    ) {
      observer.observe(node);
    }
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [target]);

  // `hole` survives a target going away, so the target is checked too rather than cleared in state.
  if (!target || !hole) {
    return null;
  }

  return createPortal(
    <>
      <div
        aria-hidden='true'
        className={`top-x0 left-x0 background-primary-inverse z-modal fixed h-screen w-screen opacity-35 ${className}`}
        data-testid='spotlight-scrim'
        role='presentation'
        style={{clipPath: keyhole(hole, halo, viewport, radius)}}
      />
      {interactive ? null : (
        <div
          aria-hidden='true'
          className='z-modal fixed'
          data-testid='spotlight-blocker'
          role='presentation'
          style={{
            top: Math.max(0, hole.top - halo),
            left: Math.max(0, hole.left - halo),
            width: hole.width + halo * 2,
            height: hole.height + halo * 2,
            // Matches the hole, so the blocker never covers a lit corner the clip-path cut away.
            borderRadius: radius ?? (hole.radius > 0 ? hole.radius + halo : 0)
          }}
        />
      )}
    </>,
    document.body
  );
};

export default Spotlight;
